import { useLazyQuery, useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

import {
  CHECK_AUTH,
  GET_DB_SNAPSHOT,
  GET_KPI
} from 'data/layoutcreator/queries/admin'
import AddAssetModal from 'screens/Admin/components/AddAssetModal'
import { BulkDeleteProjectsModal } from 'screens/Admin/components/BulkDeleteProjectsModal'
import ChangeProjectMetaModal from 'screens/Admin/components/ChangeProjectMetaModal'
import FixPagesWithWrongIdMetaModal from 'screens/Admin/components/FixPagesWithWrongIdMetaModal'
import RemoveDuplicatePagesMetaModal from 'screens/Admin/components/RemoveDuplicatePagesMetaModal'
import RemoveNonMatchingPagesMetaModal from 'screens/Admin/components/RemoveNonMatchingPagesMetaModal'
import RemovePagesMetaModal from 'screens/Admin/components/RemovePagesMetaModal'
import Flex from 'shared/ui/Flex'
import Text from 'shared/ui/Text'

import ActionButton from './components/ActionButton'
import FeatureFlags from './components/FeatureFlags'
import KPIItem, { KPIValueSizes } from './components/KPIItem'

const KPI_MIN_PAGES_COUNT = 31

const AdminTab = () => {
  const navigate = useNavigate()

  const lastGroupId = localStorage.getItem('adminLastGroupId')
  const groupId = lastGroupId || ''
  const [isAddAssetModalOpen, setIsAddAssetModalOpen] =
    useState(false)
  const [
    isChangeProjectMetaModalOpen,
    setIsChangeProjectMetaModalOpen
  ] = useState(false)
  const [
    isRemoveDuplicatePagesMetaModalOpen,
    setIsRemoveDuplicatePagesMetaModalOpen
  ] = useState(false)
  const [
    isFixPagesWithWrongIdMetaModalOpen,
    setIsFixPagesWithWrongIdMetaModalOpen
  ] = useState(false)
  const [isRemovePagesMetaModalOpen, setIsRemovePagesMetaModalOpen] =
    useState(false)
  const [
    isRemoveNonMatchingPagesMetaModalOpen,
    setIsRemoveNonMatchingPagesMetaModalOpen
  ] = useState(false)
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] =
    useState(false)

  const { data: kpiData } = useQuery(GET_KPI, {
    variables: { minPagesCount: KPI_MIN_PAGES_COUNT }
  })
  const [checkAuth] = useLazyQuery(CHECK_AUTH)
  const [fetchDbSnapshot] = useLazyQuery(GET_DB_SNAPSHOT)

  useEffect(() => {
    const check = async () => {
      const { data } = await checkAuth()
      if (!data?.checkAuth) {
        navigate('/')
      }
    }

    check()
  }, [checkAuth, navigate])

  const handleDownloadDbSnapshotClick = async () => {
    const toastId = toast.loading('Downloading db snapshot...', {
      icon: '🔧'
    })
    const { data, error } = await fetchDbSnapshot({
      variables: { groupId }
    })

    if (error) {
      toast.error('Error fetching db data')
      toast.dismiss(toastId)
      return
    }

    const blob = new Blob(
      [data?.getDbSnapshot || 'Error fetching db data'],
      { type: 'text/json' }
    )

    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.setAttribute('download', `db-snapshot-${groupId}.json`)
    link.setAttribute('href', url)
    document.body.appendChild(link)
    link.click()

    toast.dismiss(toastId)

    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const KPIS = [
    {
      value: kpiData?.getKpi?.pagesCreated || 0,
      label: 'Pages \n created',
      size: KPIValueSizes.sm
    },
    {
      value: kpiData?.getKpi?.pagesExported || 0,
      label: 'Pages \n exported',
      size: KPIValueSizes.sm
    },
    {
      value: kpiData?.getKpi?.projectsCreatedWithMinPagesCount || 0,
      label: `Projects created \n with >30 pages`,
      size: KPIValueSizes.lg
    },
    {
      value: kpiData?.getKpi?.projectsCreated || 0,
      label: 'Projects \n created',
      size: KPIValueSizes.md
    },
    {
      value: kpiData?.getKpi?.activeGroups || 0,
      label: 'Active \n groups',
      size: KPIValueSizes.md
    }
  ]

  return (
    <>
      <Flex direction="column">
        <Flex direction="column" marginTop={58}>
          <Text text="More admin actions" size={28} weight={600} />

          <Flex marginTop={36} columnGap={16} wrap="wrap" rowGap={16}>
            <ActionButton
              textId="Download DB snapshot"
              onClick={handleDownloadDbSnapshotClick}
            />

            <ActionButton
              textId="Change project meta"
              onClick={() => setIsChangeProjectMetaModalOpen(true)}
            />

            <ActionButton
              textId="Fix pages with wrong IDs"
              onClick={() =>
                setIsFixPagesWithWrongIdMetaModalOpen(true)
              }
            />

            <ActionButton
              textId="Remove pages meta"
              onClick={() => setIsRemovePagesMetaModalOpen(true)}
            />

            <ActionButton
              textId="Remove duplicate pages meta"
              onClick={() =>
                setIsRemoveDuplicatePagesMetaModalOpen(true)
              }
            />

            <ActionButton
              textId="Bulk delete projects"
              onClick={() => setIsBulkDeleteModalOpen(true)}
            />
          </Flex>
        </Flex>

        <Flex direction="column" marginTop={88}>
          <Text text="KPI Dashboard" size={28} weight={600} />

          <Flex marginTop={64} columnGap={16}>
            {KPIS.map((kpi, index) => (
              <KPIItem
                key={index}
                value={kpi.value}
                label={kpi.label}
                valueSize={kpi.size}
              />
            ))}
          </Flex>
        </Flex>
      </Flex>

      <FeatureFlags />

      <AddAssetModal
        isOpen={isAddAssetModalOpen}
        closeModal={() => setIsAddAssetModalOpen(false)}
      />

      <ChangeProjectMetaModal
        isOpen={isChangeProjectMetaModalOpen}
        closeModal={() => setIsChangeProjectMetaModalOpen(false)}
        groupId={groupId}
      />

      <RemoveDuplicatePagesMetaModal
        isOpen={isRemoveDuplicatePagesMetaModalOpen}
        closeModal={() =>
          setIsRemoveDuplicatePagesMetaModalOpen(false)
        }
      />

      <FixPagesWithWrongIdMetaModal
        isOpen={isFixPagesWithWrongIdMetaModalOpen}
        closeModal={() =>
          setIsFixPagesWithWrongIdMetaModalOpen(false)
        }
      />

      <RemovePagesMetaModal
        isOpen={isRemovePagesMetaModalOpen}
        closeModal={() => setIsRemovePagesMetaModalOpen(false)}
      />

      <RemoveNonMatchingPagesMetaModal
        isOpen={isRemoveNonMatchingPagesMetaModalOpen}
        closeModal={() =>
          setIsRemoveNonMatchingPagesMetaModalOpen(false)
        }
      />

      <BulkDeleteProjectsModal
        isOpen={isBulkDeleteModalOpen}
        onClose={() => setIsBulkDeleteModalOpen(false)}
      />
    </>
  )
}

export default AdminTab
